import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  LayoutDashboard, FolderKanban, Users, ClipboardCheck, ScrollText,
  Settings, LogOut, Menu, UserCircle, PlusCircle, Building2, ShieldCheck, FileText,
} from "lucide-react";

const NAV = {
  super_admin: [
    ["/dashboard", "nav.dashboard", LayoutDashboard, "dashboard"],
    ["/projects", "nav.projects", FolderKanban, "projects"],
    ["/review", "nav.reviewQueue", ClipboardCheck, "review-queue"],
    ["/customers", "nav.customers", Building2, "customers"],
    ["/users", "nav.users", Users, "users"],
    ["/permissions", "permissions.navLabel", ShieldCheck, "permissions"],
    ["/forms", "forms.navLabel", FileText, "forms"],
    ["/audit", "nav.auditLogs", ScrollText, "audit-logs"],
    ["/settings", "nav.zohoSettings", Settings, "zoho-settings"],
  ],
  admin: [
    ["/dashboard", "nav.dashboard", LayoutDashboard, "dashboard"],
    ["/projects", "nav.projects", FolderKanban, "projects"],
    ["/customers", "nav.customers", Building2, "customers"],
    ["/users", "nav.users", Users, "users"],
  ],
  project_manager: [
    ["/dashboard", "nav.dashboard", LayoutDashboard, "dashboard"],
    ["/projects", "nav.projects", FolderKanban, "projects"],
    ["/projects/new", "nav.newProject", PlusCircle, "new-project"],
    ["/customers", "nav.customers", Building2, "customers"],
  ],
  technician: [
    ["/dashboard", "nav.dashboard", LayoutDashboard, "dashboard"],
    ["/projects", "nav.myTasks", FolderKanban, "my-tasks"],
  ],
  supervisor: [
    ["/dashboard", "nav.dashboard", LayoutDashboard, "dashboard"],
    ["/review", "nav.reviewQueue", ClipboardCheck, "review-queue"],
    ["/projects", "nav.projects", FolderKanban, "projects"],
  ],
  customer: [
    ["/dashboard", "nav.dashboard", LayoutDashboard, "dashboard"],
    ["/projects", "nav.fulfillment", FolderKanban, "fulfillment"],
  ],
};

function NavItems({ items, onNavigate }) {
  const { t } = useTranslation();
  return (
    <nav className="flex flex-col gap-1">
      {items.map(([to, labelKey, Icon, slug]) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/projects"}
          onClick={onNavigate}
          data-testid={`nav-${slug}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive
                ? "bg-blue-600 text-white"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`
          }
        >
          <Icon className="h-[18px] w-[18px]" />
          {t(labelKey)}
        </NavLink>
      ))}
    </nav>
  );
}

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const items = NAV[user?.role] || [];

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const Brand = (
    <div className="px-3 py-4">
      <div className="rounded-lg bg-white px-3 py-2.5">
        <img src="/logo.webp" alt="The Retrofitter" className="h-7 w-auto" />
      </div>
      <div className="overline mt-2 pl-1 text-slate-500">{t("layout.projectManagement")}</div>
    </div>
  );

  const SidebarInner = ({ onNavigate }) => (
    <div className="flex h-full flex-col">
      {Brand}
      <div className="flex-1 px-3">
        <NavItems items={items} onNavigate={onNavigate} />
      </div>
      <div className="border-t border-slate-800 p-3">
        <NavLink
          to="/profile"
          onClick={onNavigate}
          data-testid="nav-profile"
          className="mb-2 flex items-center gap-3 rounded-md px-3 py-2 text-sm text-slate-300 hover:bg-slate-800"
        >
          <UserCircle className="h-[18px] w-[18px]" />
          <div className="truncate">
            <div className="truncate font-medium text-white">{user?.name}</div>
            <div className="overline whitespace-normal leading-tight text-slate-500">{user?.role ? t(`roles.${user.role}`) : ""}</div>
          </div>
        </NavLink>
        <Button
          data-testid="logout-btn"
          onClick={doLogout}
          variant="ghost"
          className="w-full justify-start gap-3 text-slate-400 hover:bg-red-500/10 hover:text-red-400"
        >
          <LogOut className="h-[18px] w-[18px]" /> {t("layout.signOut")}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900">
      <aside className="hidden w-64 shrink-0 bg-slate-900 lg:block">
        <SidebarInner />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" className="text-slate-700 lg:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 border-slate-800 bg-slate-900 p-0">
              <SidebarInner onNavigate={() => {}} />
            </SheetContent>
          </Sheet>
          <div className="rounded-md bg-slate-900 px-2 py-1.5 lg:hidden">
            <img src="/logo.webp" alt="The Retrofitter" className="h-5 w-auto" />
          </div>
          <LanguageSwitcher className="ml-auto" triggerClassName="border-slate-300 bg-white text-slate-700" />
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
